<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

import type {
  CssCustomPropertyFont,
  CssCustomPropertyColor,
} from '../../helpers/cssCustomProperties';
import {
  getStringStyles,
  getCssCustomPropertyValue,
} from '../../helpers/cssCustomProperties';

interface AppRouterLinkProps {
  link: string;
  font: CssCustomPropertyFont;
  color: CssCustomPropertyColor;
  wide?: boolean;
}

const props = defineProps<AppRouterLinkProps>();

interface AppRouterLinkEmits {
  // NOTE: This event is meant to be used with telemetry events when navigation is triggered
  (eventName: 'navigation-triggered'): void;
  (eventName: 'capture-click'): void;
}

const emit = defineEmits<AppRouterLinkEmits>();

const style = computed(() =>
  getStringStyles(props.color, getCssCustomPropertyValue)
);

function onClick(event: MouseEvent): void {
  event.stopPropagation();
  emit('navigation-triggered');
}

function onCaptureClick(): void {
  emit('capture-click');
}
</script>

<template>
  <!-- In some cases, we need to execute a function before navigating to a route. 
   That’s why this `click.capture` has been added—specifically for the redirect of *ownStay*, 
   as we need to add an accommodation ingredient before navigating to the hotel selection.-->
  <button
    class="app-router-link"
    :class="{ wide: props.wide }"
    @click="onClick"
    @click.capture="onCaptureClick"
  >
    <RouterLink :class="[props.font]" :to="props.link" :style="style">
      <slot />
    </RouterLink>
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_media.scss';

.app-router-link {
  all: unset;
  &.wide {
    display: grid;
    width: 100%;
    a {
      @include on-hover {
        text-decoration: none;
      }
    }
  }
  a {
    display: inline-block;
    text-decoration: none;

    @include on-hover {
      cursor: pointer;
      text-decoration: underline;
    }
  }
}
</style>
